import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Award, ShieldCheck, Wallet, FileCheck2 } from "lucide-react";
import api from "@/lib/api";

const KIND_META = {
  rank: { icon: Award, color: "text-amber-600 bg-amber-50" },
  profile: { icon: ShieldCheck, color: "text-emerald-700 bg-emerald-50" },
  withdrawal: { icon: Wallet, color: "text-blue-700 bg-blue-50" },
  lead: { icon: FileCheck2, color: "text-emerald-700 bg-emerald-50" },
};

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* user unauthenticated */ void 0; }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.put(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };
  const markAll = async () => {
    await api.put("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="notification-bell"
        className="relative p-2 hover:bg-stone-100 rounded-full transition-colors">
        <Bell className="w-5 h-5 text-stone-700" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-black rounded-full w-4 h-4 grid place-items-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl border border-stone-200 shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="px-5 py-3.5 border-b border-stone-200 flex items-center justify-between">
            <div className="font-display font-black text-lg">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs font-bold text-emerald-900 hover:text-emerald-950 flex items-center gap-1" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 px-6 text-center text-stone-500 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
                You&apos;re all caught up.
              </div>
            ) : (
              items.map((n) => {
                const meta = KIND_META[n.kind] || KIND_META.profile;
                const Icon = meta.icon;
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-${n.id}`}
                    className={`w-full text-left px-5 py-3.5 border-b border-stone-100 last:border-0 hover:bg-stone-50 flex gap-3 items-start ${!n.read ? "bg-amber-50/40" : ""}`}>
                    <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-stone-900">{n.title}</div>
                      <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="text-[10px] text-stone-400 font-mono mt-1.5 uppercase tracking-wider">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-amber-500 mt-2 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
